import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  Clock,
  Plus,
  Edit,
  Trash2,
  X,
} from 'lucide-react';
import './TimetablePage.css';

const TimetablePage = () => {
  // State management
  const [selectedClass, setSelectedClass] = useState('5');
  const [selectedSection, setSelectedSection] = useState('A');
  const [timetable, setTimetable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    day: 'Monday',
    period: 1,
    subject: '',
    teacher: '',
    room: '',
    startTime: '',
    endTime: '',
  });

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const periods = [1, 2, 3, 4, 5, 6, 7, 8];
  const classes = Array.from({ length: 10 }, (_, i) => (i + 1).toString());
  const sections = ['A', 'B'];

  // Fetch timetable
  useEffect(() => {
    fetchTimetable();
  }, [selectedClass, selectedSection]);

  const fetchTimetable = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/timetable/class/${selectedClass}/${selectedSection}`);
      const data = response.data.items || response.data;
      setTimetable(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching timetable:', err);
      setTimetable([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPeriod = () => {
    setEditingId(null);
    setFormData({
      day: 'Monday',
      period: 1,
      subject: '',
      teacher: '',
      room: '',
      startTime: '',
      endTime: '',
    });
    setShowModal(true);
  };

  const handleEditPeriod = (period) => {
    setEditingId(period.id);
    setFormData({
      day: period.day,
      period: period.period,
      subject: period.subject || '',
      teacher: period.teacher || '',
      room: period.room || '',
      startTime: period.startTime || '',
      endTime: period.endTime || '',
    });
    setShowModal(true);
  };

  const handleDeletePeriod = async (periodId) => {
    try {
      await api.delete(`/timetable/${periodId}`);
      setTimetable(timetable.filter((p) => p.id !== periodId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting period:', err);
      setError('Failed to delete period');
    }
  };

  const handleSavePeriod = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        class: selectedClass,
        section: selectedSection,
      };

      if (editingId) {
        await api.put(`/timetable/${editingId}`, payload);
      } else {
        await api.post('/timetable', payload);
      }

      setShowModal(false);
      fetchTimetable();
    } catch (err) {
      console.error('Error saving period:', err);
      setError('Failed to save period');
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) : value,
    }));
  };

  const getPeriodForDayAndPeriod = (day, period) => {
    return timetable.find((p) => p.day === day && p.period === period);
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: '#111827' }}>
            Class Timetable
          </h1>
          <p style={{ margin: 0, color: '#6b7280' }}>Manage weekly class schedule</p>
        </div>

        {/* Class/Section Selector */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr auto',
            gap: '16px',
            marginBottom: '24px',
            alignItems: 'end',
          }}
        >
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
              Class
            </label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            >
              {classes.map((cls) => (
                <option key={cls} value={cls}>
                  Class {cls}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
              Section
            </label>
            <select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            >
              {sections.map((sec) => (
                <option key={sec} value={sec}>
                  Section {sec}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleAddPeriod}
            style={{
              padding: '10px 16px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Plus size={18} />
            Add Period
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {/* Timetable Grid */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            overflow: 'x-auto',
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              Loading timetable...
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <th
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#111827',
                      }}
                    >
                      Period
                    </th>
                    {days.map((day) => (
                      <th
                        key={day}
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#111827',
                          minWidth: '140px',
                        }}
                      >
                        {day}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {periods.map((period) => (
                    <tr key={period} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td
                        style={{
                          padding: '12px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#111827',
                          backgroundColor: '#f9fafb',
                        }}
                      >
                        Period {period}
                      </td>
                      {days.map((day) => {
                        const cellData = getPeriodForDayAndPeriod(day, period);
                        return (
                          <td
                            key={`${day}-${period}`}
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              fontSize: '13px',
                              minWidth: '140px',
                              position: 'relative',
                            }}
                          >
                            {cellData ? (
                              <div
                                style={{
                                  backgroundColor: '#dbeafe',
                                  borderRadius: '6px',
                                  padding: '8px',
                                  position: 'relative',
                                  group: 'hover',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#bfdbfe';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#dbeafe';
                                }}
                              >
                                <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>
                                  {cellData.subject}
                                </div>
                                {cellData.teacher && (
                                  <div style={{ fontSize: '12px', color: '#3730a3', marginBottom: '2px' }}>
                                    {cellData.teacher}
                                  </div>
                                )}
                                {cellData.room && (
                                  <div style={{ fontSize: '12px', color: '#3730a3', marginBottom: '4px' }}>
                                    Room {cellData.room}
                                  </div>
                                )}
                                {cellData.startTime && (
                                  <div style={{ fontSize: '11px', color: '#4c1d95', marginBottom: '6px' }}>
                                    {cellData.startTime} - {cellData.endTime}
                                  </div>
                                )}
                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                  <button
                                    onClick={() => handleEditPeriod(cellData)}
                                    style={{
                                      padding: '4px 6px',
                                      backgroundColor: '#3b82f6',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                    }}
                                  >
                                    <Edit size={12} />
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirm(cellData.id)}
                                    style={{
                                      padding: '4px 6px',
                                      backgroundColor: '#ef4444',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                    }}
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div
                                style={{
                                  color: '#d1d5db',
                                  fontSize: '12px',
                                }}
                              >
                                -
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '400px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px' }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Delete Period?
              </h2>
              <p style={{ margin: '0 0 16px 0', color: '#6b7280' }}>
                This action cannot be undone. Are you sure you want to delete this period?
              </p>
            </div>
            <div
              style={{
                padding: '16px 24px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeletePeriod(deleteConfirm)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Period Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                {editingId ? 'Edit Period' : 'Add Period'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSavePeriod}>
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Day
                    </label>
                    <select
                      name="day"
                      value={formData.day}
                      onChange={handleFormChange}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    >
                      {days.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Period
                    </label>
                    <select
                      name="period"
                      value={formData.period}
                      onChange={handleFormChange}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    >
                      {periods.map((period) => (
                        <option key={period} value={period}>
                          Period {period}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleFormChange}
                    placeholder="Enter subject name"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Teacher Name
                  </label>
                  <input
                    type="text"
                    name="teacher"
                    value={formData.teacher}
                    onChange={handleFormChange}
                    placeholder="Enter teacher name"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Room
                  </label>
                  <input
                    type="text"
                    name="room"
                    value={formData.room}
                    onChange={handleFormChange}
                    placeholder="Enter room number"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Start Time
                    </label>
                    <input
                      type="time"
                      name="startTime"
                      value={formData.startTime}
                      onChange={handleFormChange}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      End Time
                    </label>
                    <input
                      type="time"
                      name="endTime"
                      value={formData.endTime}
                      onChange={handleFormChange}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  padding: '16px 24px',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {editingId ? 'Update Period' : 'Add Period'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimetablePage;
